import { EstateDesignType } from "@1games-ru/region-shared/dist/enums/estate/estate";
import { useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import clsx from "clsx";
import useAppDispatch from "@/core/helpers/useAppDispatch";
import useAppSelector from "@/core/helpers/useAppSelector";
import { BankCardIcon, EyeIcon } from "@/shared/icons";
import { Button } from "@/shared/ui";
import DeviceTabContentWrapper from "@/shared/widgets/DeviceTabContentWrapper";
import {
  designPreviewEnabledSelector,
  estateSelector,
  selectedDesignVariantSelector,
  setDesignOpenConfirmBuy,
  setSelectedDesignVariant,
} from "@/domains/estate/slice";
import { playerInfoSelector } from "@/domains/player/slice";
import useDesignHandlers from "@/domains/estate/hooks/handlers/useDesignHandlers";
import useLoadDesign from "@/domains/estate/hooks/useLoadDesign";
import EstateDesignListContainer from "@/domains/estate/containers/tablet/tabs/design/EstateDesignListContainer";
import EstateDesignVariantBuyConfirmModalContainer from "@/domains/estate/containers/tablet/tabs/design/EstateDesignVariantBuyConfirmModalContainer.tsx";
import EstateDesignVariantView from "@/domains/estate/components/design/EstateDesignVariantView";

type Props = {
  type: EstateDesignType;
  onClose: () => void;
};

export default function EstateDesignContainer({ type, onClose }: Props) {
  const { t } = useTranslation("estate");
  const dispatch = useAppDispatch();
  const { previewHandler, isPreviewing, isBuying } = useDesignHandlers();
  const estate = useAppSelector(estateSelector);
  const selectedDesignVariant = useAppSelector(selectedDesignVariantSelector);
  const previewEnabled = useAppSelector(designPreviewEnabledSelector);
  const playerInfo = useAppSelector(playerInfoSelector);

  const { loadDesign } = useLoadDesign();

  useEffect(() => {
    loadDesign();
  }, [loadDesign]);

  useEffect(() => {
    return () => {
      dispatch(setSelectedDesignVariant());
    };
  }, [dispatch]);

  const disableFooterButtons = useMemo(() => isBuying || isPreviewing, [isBuying, isPreviewing]);

  if (!playerInfo?.character) return null;
  if (!estate) return null;

  const header = selectedDesignVariant ? (
    <div className="flex items-center gap-2.5">
      <Button
        variant="soft"
        color="gray"
        className="p-0 w-[2rem] h-[2rem] shrink-0 font-arial text-[0.7rem]"
        onClick={() => dispatch(setSelectedDesignVariant(null))}
      >
        &#9668;
      </Button>
      <div className="text-18 text-white font-bold">
        {t(`estate.design.designVariants.${selectedDesignVariant.name}.title`)}
      </div>
    </div>
  ) : (
    t(`estate.design.${type.toLowerCase()}.title`)
  );

  const footer = selectedDesignVariant ? (
    <div
      className={clsx(
        "w-full grid items-center gap-2",
        previewEnabled ? "grid-cols-2" : "grid-col-1",
      )}
    >
      <Button
        className="w-full h-[2.75rem] flex items-center gap-2.5"
        onClick={() => dispatch(setDesignOpenConfirmBuy(true))}
        disabled={
          disableFooterButtons ||
          playerInfo.character.money.bank < selectedDesignVariant.price ||
          selectedDesignVariant.isActive
        }
      >
        <BankCardIcon size={18} />
        {t("estate.design.pay")}
      </Button>
      {previewEnabled && (
        <Button
          variant="soft"
          color="gray"
          className="gap-2 w-full h-[2.75rem]"
          onClick={previewHandler}
          loading={isPreviewing}
          disabled={disableFooterButtons || selectedDesignVariant.isActive}
        >
          <EyeIcon size={20} />
          {t("estate.design.preview")}
        </Button>
      )}
    </div>
  ) : null;

  return (
    <DeviceTabContentWrapper header={header} footer={footer} onClose={onClose} className="p-0">
      {selectedDesignVariant ? (
        <EstateDesignVariantView
          uuid={selectedDesignVariant.uuid}
          name={selectedDesignVariant.name}
          images={selectedDesignVariant.images}
          price={selectedDesignVariant.price}
          isActive={selectedDesignVariant.isActive}
        />
      ) : (
        <EstateDesignListContainer type={type} />
      )}
      <EstateDesignVariantBuyConfirmModalContainer />
    </DeviceTabContentWrapper>
  );
}
